import { useMemo } from 'react';
import {
  LayoutDashboard,
  Briefcase,
  Wallet,
  ClipboardCheck,
  User,
  Search,
  LifeBuoy,
} from 'lucide-react';
import { selectUserState } from 'src/lib/features/auth/authSlice';
import { useTypedSelector } from 'src/lib/store/store';

/**
 * The reference splits the talent rail into two labelled groups: the primary
 * "My Employment" set, and a muted secondary set for outbound/ancillary links.
 * The primary group is also what the mobile bottom tab bar renders.
 */
export const PRIMARY_NAV = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    path: '/dashboard',
    icon: LayoutDashboard,
    /* offers, invites and matched jobs all live under the dashboard */
    also: ['/', '/alloffer', '/invites', '/jobs'],
  },
  { key: 'project', label: 'Projects', path: '/project', icon: Briefcase },
  {
    key: 'wallet',
    label: 'Wallet',
    path: '/wallet',
    icon: Wallet,
    also: ['/transactions'],
  },
  { key: 'test', label: 'Test', path: '/test', icon: ClipboardCheck },
  { key: 'profile', label: 'Profile', path: '/profile', icon: User },
];

/** Small labels for the sticky header, per the reference's path→label map. */
export const PAGE_TITLES = {
  '/': 'Dashboard',
  '/dashboard': 'Dashboard',
  '/profile': 'Profile',
  '/alloffer': 'All Offers',
  '/jobs': 'Matched Jobs',
  '/invites': 'Invites',
  '/project': 'Projects',
  '/projects': 'Active Projects',
  '/wallet': 'Wallet',
  '/wallet/withdraw': 'Withdraw',
  '/wallet/confirm-withdrawal': 'Confirm Withdrawal',
  '/transactions': 'Transactions',
  '/support': 'Support',
  '/test': 'Test',
};

export const pageTitleFor = (pathname) => {
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname];
  const match = Object.keys(PAGE_TITLES)
    .filter((p) => p !== '/' && pathname.startsWith(p))
    .sort((a, b) => b.length - a.length)[0];
  return match ? PAGE_TITLES[match] : 'Droomwork';
};

/**
 * Reference active matcher: exact match, or a prefix match for anything that
 * is not the home route (so detail pages keep their parent lit).
 */
export const isNavItemActive = (item, pathname) => {
  if (pathname === item.path) return true;
  if (pathname.startsWith(`${item.path}/`)) return true;
  return (item.also ?? []).some(
    (p) => pathname === p || (p !== '/' && pathname.startsWith(`${p}/`))
  );
};

/** "Find jobs" points at the external job board and carries the access token. */
export const useNavGroups = () => {
  const userState = useTypedSelector(selectUserState);

  const secondary = useMemo(
    () => [
      {
        key: 'find-jobs',
        label: 'Find jobs',
        path: `${process.env.REACT_APP_JOB_URL}?token=${userState?.accessToken}`,
        icon: Search,
        external: true,
      },
      { key: 'support', label: 'Support', path: '/support', icon: LifeBuoy },
    ],
    [userState]
  );

  return { primary: PRIMARY_NAV, secondary };
};
